import * as amplitude from '@amplitude/analytics-browser';
import { sessionReplayPlugin } from '@amplitude/plugin-session-replay-browser';

import { resolveAmplitudeApiKey, resolveAnalyticsEnvironment } from './amplitude-api-key';
import { rememberAmplitudeBlocked, resolveAmplitudeProxyUrls } from './amplitude-transport';
import { appPlatformPlugin } from './app-platform-plugin';
import { appSectionPlugin } from './app-section-plugin';
import { detectAdBlocker } from './detect-ad-blocker';
import { isHeadlessRoute } from './is-headless-route';
import { urlRedactionPlugin } from './url-redaction-plugin';

/**
 * Флаг живёт в модуле: `initAmplitude` зовётся из `src/index.tsx` вне дерева React, и повторный вызов
 * (горячая перезагрузка модуля на дев-сервере) не должен поднимать SDK второй раз.
 */
let initialized = false;

/** User property: прямой запрос к Amplitude отрезан — доля блокировщиков среди тех, кто дошёл (через прокси). */
export const HAS_AD_BLOCKER_PROPERTY = 'has_ad_blocker';

export function initAmplitude(): void {
  if (initialized) {
    return;
  }

  if (isHeadlessRoute(window.location.pathname)) {
    return;
  }

  const environment = resolveAnalyticsEnvironment();
  const apiKey = resolveAmplitudeApiKey(environment);

  if (!apiKey) {
    // Окружение в тексте обязательно: без него непонятно, какую из трёх переменных потеряли.
    // eslint-disable-next-line no-console
    console.warn(`Amplitude API key missing for environment "${environment}" — analytics disabled`);
    return;
  }

  initialized = true;

  // Плагины регистрируются до `init`: `add` до готовности клиента встаёт в ту же очередь,
  // что и события (`core-client.js`), поэтому обогащение успевает встать раньше первых
  // autocapture-событий — иначе `Page Viewed` при загрузке уехал бы без метки. Session Replay
  // по документации Amplitude тоже добавляется до `init`.
  // Прокси — только там, где прямой путь уже был отрезан (`amplitude-transport.ts`).
  const proxy = resolveAmplitudeProxyUrls(environment, window.location.origin);

  amplitude.add(appPlatformPlugin());
  amplitude.add(appSectionPlugin());
  amplitude.add(
    sessionReplayPlugin({
      sampleRate: 1,
      ...(proxy && { trackServerUrl: proxy.trackServerUrl, configServerUrl: proxy.configServerUrl }),
    }),
  );

  // `init`, а не `initAll` из `@amplitude/unified`: тот безусловно поднимал ещё Experiment и
  // Guides & Surveys, которыми мы не пользуемся, — лишние запросы и лишний код (IMGM-1609).
  const initialization = amplitude.init(apiKey, {
    autocapture: true,
    // `minIdLength: 1` — иначе сервер Amplitude отклоняет события пользователей с id короче
    // пяти символов: серверный дефолт минимальной длины — 5, а SDK уезжает с ним в payload
    // каждого запроса (`analytics-core/lib/cjs/plugins/destination.js:220`). Ставим id как
    // есть, без префикса, чтобы в интерфейсе Amplitude он совпадал с id в админке.
    minIdLength: 1,
    ...(proxy && { serverUrl: proxy.serverUrl }),
  });

  // Чистка секретов из адреса — последним плагином, после тех, что `init` ставит сам (адрес страницы, `referrer`);
  // добавленная до `init`, она шла бы раньше них (`url-redaction-plugin.ts`). Первые события не проскакивают:
  // накопленное до готовности SDK шлёт в очередь в конце `init`, а очередь применяет плагины по `setTimeout(0)`
  // (`analytics-core/lib/esm/timeline.js`) — регистрация здесь идёт микрозадачами и успевает раньше.
  void initialization.promise.then(() => amplitude.add(urlRedactionPlugin()).promise);

  // Фоном, не задерживая старт: `init` ждать пробу не может — до него пуст `getSessionId()`, и
  // `Application Opened` бы потерялся. Итог пробы решает путь со следующей загрузки; неотправленное
  // SDK хранит и досылает при следующем запуске.
  void detectAdBlocker().then((blocked) => {
    rememberAmplitudeBlocked(blocked);
    amplitude.identify(new amplitude.Identify().set(HAS_AD_BLOCKER_PROPERTY, blocked));
  });
}
